import dayjs from "dayjs";

import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Switch,
  Typography,
} from "@mui/material";

import {
  ApiDealsRoutesItem,
  ApiGetFlightRoutesSampleParams,
  getDealsFlightRoutes,
} from "@/api/routes";
import AlertModal, { AlertModalRefProps } from "@/components/AlertModal";
import DateFastForward from "@/components/DateFastForward";
import IntroDeals from "@/components/IntroDeals";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import { AlertTypeEnum } from "@/constants";
import { ROUTES_PAGE_INIT_LOADDATA } from "@/constants/eventName";
import { FREE_PANORAMA_RANGE, OPEN_ALL_EXPLORE_DATA } from "@/constants/membership";
import { BANK_NAME_MAP, PROGRAM_NAME_MAP } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";
import useMore from "@/hooks/useMore";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { ConditionType, createSearchQuery } from "@/pages/Routes/hooks/useCondition";
import { CabinsType } from "@/typings";
import { getBankNameByProgram } from "@/utils";

import FilterBar from "./components/FilterBar";
import FlightDetailsModal from "./components/FlightDetails";
import MobileSearchBar from "./components/MobileSearchBar";
import RoutesList from "./components/RoutesList";
import RoutesListSkeleton from "./components/RoutesListSkeleton";
import SearchBar from "./components/SearchBar";
import Sort from "./components/Sort";
import { sortFn } from "./components/Sort/sortConfig";
import useCheckCondition from "./hooks/useCheckCondition";
import { LocationEnum } from "./hooks/useLocationFilter";
import useResponseCount from "./hooks/useResponseCount";

const RoutesPage = () => {
  const theme = useThemeData();
  const { isMobile, isPc } = usePlatform();
  const { checkCondition } = useCheckCondition();
  const [, setSearchParams] = useSearchParams();
  const store = useGlobalStoreContext();
  const show_upsell: boolean = store?.show_upsell && !OPEN_ALL_EXPLORE_DATA;

  const closestAirportRef = useRef(store?.closest_airport);

  closestAirportRef.current = store?.closest_airport;

  const [condition, setCondition] = useState<ConditionType>();
  const [searchData, setSearchData] = useState<ApiDealsRoutesItem[]>([]);
  const [filterData, setFilterData] = useState<ApiDealsRoutesItem[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [currentParams, setCurrentParams] = useState<
    ApiGetFlightRoutesSampleParams & { type: CabinsType }
  >();
  const [totalTitle, setTotalTitle] = useState("");
  const [trackPriceOpen, setTrackPriceOpen] = useState(false);

  const navigate = useNavigate();

  const limitCount = useResponseCount();

  const [dialogOpen, setDialogOpen] = useState(false);

  const modalRef = useRef<AlertModalRefProps>(null);

  useListenEvent(ROUTES_PAGE_INIT_LOADDATA, (data: ConditionType) => {
    if (data.fromLocation.length !== 0 && data.toLocation.length !== 0) {
      setCondition(data);
      handleSearch(data);
    }
  });
  const { historyData, handleNext, itemLeft, hasMore } = useMore(filterData, {
    pageSize: 24,
  });

  const handleSearch = useEvent(async (data: ConditionType, isClick?: boolean) => {
    if (data) {
      const errMsg = checkCondition(data);

      if (errMsg) {
        setLoading(false);
        return;
      }

      const nextParams = createSearchQuery(data);
      console.log(nextParams);

      const nextSearch = createSearchParams(nextParams).toString();

      setSearchParams(nextSearch);
      setCondition(data);

      const fromLocation = {
        regions: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };
      const toLocation = {
        regions: data.toLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.toLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.toLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };

      const params = {
        date_range: {
          from: dayjs(data.startDate).format("YYYY-MM-DD"),
          to: dayjs(data.endDate).format("YYYY-MM-DD"),
        },
        origins: fromLocation,
        arrivals: toLocation,
      };

      try {
        // 手动出发搜索按钮时缓存搜索条件到本地
        if (isClick) {
          localStorage.setItem("deals_routes_search_conditions", JSON.stringify(data));
        }

        setLoading(true);
        setSearchData([]);
        await getDealsFlightRoutes(
          params,
          appendSearchData,
          setDialogOpen,
          setLoading,
          show_upsell
        );
      } catch (error) {
        console.log(error);
      }
    }
  });

  const appendSearchData = (data: ApiDealsRoutesItem[], stopLoading = false) => {
    if (stopLoading) {
      // const initialData = initialRouteSort(data, closestAirportRef.current);
      // setSearchData(initialData);
      setSearchData(data);
      setTimeout(() => {
        setLoading(false);
      }, 200);
    } else {
      setSearchData(data);
    }
  };

  const filterChange = useCallback(
    (v: ApiDealsRoutesItem[]) => {
      const sortFilterData = sortFn(v, sortKey);
      setFilterData(sortFilterData);
    },
    [sortKey]
  );

  const sortChange = useCallback(
    (sortKey: number) => {
      const sortFilterData = sortFn(filterData, sortKey);
      setFilterData(sortFilterData);
      setSortKey(sortKey);
    },
    [filterData]
  );

  useEffect(() => {
    let text = "";
    if (searchData.length > 0) {
      if (filterData.length === searchData.length) {
        text = `${Number(searchData.length).toLocaleString()}` + " deals";
      } else {
        text =
          `${Number(filterData.length).toLocaleString()} / ${Number(
            searchData.length
          ).toLocaleString()}` + " deals";
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [searchData, filterData]);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Deals</title>
        <meta
          name="description"
          content="Explore the world with the best millions of award flight deals."
        />
      </Helmet>
      <div className="max-w-[1320px] mx-auto mode-bg">
        {isMobile ? (
          <>
            <MobileSearchBar data={condition} onSearch={handleSearch} isLoading={isLoading} />
            <div className="px-[10px] mt-[16px]">
              <FilterBar dataSourceList={searchData} onChange={filterChange} />
            </div>
          </>
        ) : (
          <div className="px-[10px]">
            <div className="py-[16px] pb-[10px]">
              <SearchBar onSearch={handleSearch} isLoading={isLoading} />
            </div>
            <Box
              sx={{
                position: "relative",
                marginTop: "5px",
                "&::after": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  width: "100%",
                  height: "4px",
                  bottom: "-4px",
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "linear-gradient(to bottom, rgba(255, 255, 255, 0.08), transparent)"
                      : "linear-gradient(to bottom, rgba(0, 0, 0, 0.05), transparent)",
                },
              }}
            >
              <Divider className="mt-[10px] mb-[10px]" />
            </Box>
            <div className="mt-[10px] mb-[15px]" />
            <FilterBar dataSourceList={searchData} onChange={filterChange} />
          </div>
        )}

        {/* <div className="mt-[13px] mb-[3px]">
          <IntroDeals />
        </div> */}

        {isLoading ? (
          <div className="mt-[9px] mx-[10px]">
            <LinearProgress />
          </div>
        ) : null}

        {searchData.length > 0 && (
          <div className="px-[10px] mt-[-2px]">
            <div className="flex items-center py-[12px]">
              <Typography variant="body1" color="textPrimary">
                {totalTitle}
              </Typography>

              <Switch
                inputProps={{ "aria-label": "controlled" }}
                checked={trackPriceOpen}
                onChange={(e) => {
                  setTrackPriceOpen(e.target.checked);
                  modalRef.current?.show(AlertTypeEnum.Panorama, null);
                }}
              />

              <NotificationsActiveIcon color="primary" />
              <div className="flex-1" />
              <div className="flex">
                {isPc && <DateFastForward />}
                {/* <DateFastForward /> */}
                <Sort value={sortKey} onChange={sortChange} />
              </div>
            </div>
          </div>
        )}

        {filterData.length === 0 && !isLoading ? (
          <div className="mt-[80px] text-center">
            {show_upsell && (
              <div>
                <Typography color="textSecondary">
                  {"Free user can only access " + FREE_PANORAMA_RANGE + " days of data."}
                </Typography>
                <Typography color="primary">Upgrade to PRO to enjoy 365 days of data.</Typography>
              </div>
            )}
            <Typography color="textSecondary">No results match your current filters.</Typography>

            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div className="px-[10px]">
            {filterData.length ? (
              <>
                {isMobile && (
                  <div className="flex justify-end pb-[10px] mt-[-2px]">
                    <DateFastForward />
                  </div>
                )}
                <RoutesList
                  data={limitCount === -1 ? historyData : historyData.slice(0, limitCount)}
                  onClick={(v) => {
                    const config: Record<string, CabinsType> = {
                      y: "Economy",
                      w: "Premium Economy",
                      f: "First",
                      j: "Business",
                    };
                    setCurrentParams({
                      program: v.program,
                      origin: v.route.split("-")[0],
                      arrival: v.route.split("-")[1],
                      date: dayjs(v.date).format("YYYY-MM-DD"),
                      type: config[v.target_cabin],
                    });

                    setOpen(true);
                  }}
                  onAlertClick={(info) => {
                    const route = info.route.split("-");

                    const data = {
                      fromLocation: [route[0]],
                      toLocation: [route[1]],
                      departureDate: dayjs(info.date).valueOf(),
                      departureDateSec: dayjs(info.date).valueOf(),
                      stops: info.nonstop ? "nonstop" : "has stops",
                      maxPoints: info.price,
                    };
                    modalRef.current?.show(AlertTypeEnum.Panorama, data);
                  }}
                />
              </>
            ) : null}
            {hasMore ? (
              !show_upsell ? (
                <div
                  className="w-full flex items-center justify-center cursor-pointer mt-[20px] mb-[20px]"
                  onClick={handleNext}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ExpandMoreIcon />}
                    onClick={handleNext}
                    sx={{ borderRadius: "12px" }}
                  >
                    More Deals
                  </Button>
                </div>
              ) : (
                <div
                  className="w-full flex items-center justify-center cursor-pointer mt-[20px]"
                  onClick={() => {
                    window.open("/pro", "_blank", "noopener,noreferrer");
                  }}
                >
                  <Button variant="contained" disableElevation>
                    upgrade to check {Number(searchData.length - limitCount).toLocaleString()} more
                    deals
                  </Button>
                </div>
              )
            ) : null}
            {isLoading ? <RoutesListSkeleton count={limitCount === -1 ? 20 : limitCount} /> : null}
          </div>
        )}
      </div>

      {open && (
        <FlightDetailsModal open={open} onClose={() => setOpen(false)} params={currentParams} />
      )}

      <Dialog
        onClose={() => {
          setDialogOpen(false);
        }}
        open={dialogOpen}
        sx={{
          maxWidth: "750px",
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
        }}
      >
        <DialogTitle>
          <Typography color="textPrimary">Search Condition Error</Typography>
        </DialogTitle>
        <DialogContent dividers className="pt-[20px] !px-[20px]">
          <Typography variant="body1" color="error" gutterBottom>
            Your search range is too broad.
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please reduce the number of days in your search range.
          </Typography>
          <Typography variant="body2" color="textSecondary" className="mt-[10px]">
            Try searching for shorter date ranges or narrowing your location criteria.
          </Typography>
        </DialogContent>
      </Dialog>
      <ScrollTopBar />

      <AlertModal
        ref={modalRef}
        onReset={() => {
          setTrackPriceOpen(false);
        }}
      />
    </>
  );
};

export default RoutesPage;
